import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { useActiveOnScreen } from "@/lib/useActiveOnScreen";

/* Analytics — live firm intelligence mini-dashboard. */

const KPIS = [
    { id: "in",   icon: "fa-phone-volume",     label: "Inbound calls · today",  value: 187, delta: "+12%",  trend: "up",   suffix: "" },
    { id: "qual", icon: "fa-circle-check",     label: "Qualified intakes",      value: 41,  delta: "+8%",   trend: "up",   suffix: "" },
    { id: "ret",  icon: "fa-file-signature",   label: "Retainer sign-time",     value: 39,  delta: "−4.2h", trend: "down", suffix: "h" },
    { id: "rev",  icon: "fa-dollar-sign",      label: "Pipeline added · 7-day", value: 1.4, delta: "+22%",  trend: "up",   suffix: "M" },
];

const SOURCES = [
    { id: "goog",   label: "Google · Local Service Ads", value: 38, color: "#1E5BFF" },
    { id: "refer",  label: "Attorney referrals",          value: 24, color: "#7C5BFF" },
    { id: "fb",     label: "Facebook",                    value: 16, color: "#15803D" },
    { id: "tv",     label: "TV / OTT",                    value: 12, color: "#B45309" },
    { id: "org",    label: "Organic / direct",            value: 10, color: "#5C5159" },
];

const FUNNEL = [
    { id: "calls",   label: "Calls answered",        value: 1247, pct: 100 },
    { id: "intake",  label: "Intakes completed",     value: 612,  pct: 49 },
    { id: "qual",    label: "Qualified · in scope",  value: 318,  pct: 25.5 },
    { id: "signed",  label: "Retainers signed",      value: 184,  pct: 14.8 },
];

const COHORTS = [
    { id: "auto",    label: "Auto · PI",        signed: 92,  rate: 17.4, value: "$612K" },
    { id: "mt",      label: "Mass tort",        signed: 41,  rate: 11.2, value: "$420K" },
    { id: "premise", label: "Premises",         signed: 28,  rate: 13.9, value: "$184K" },
    { id: "estate",  label: "Estate · planning", signed: 23,  rate: 22.1, value: "$92K"  },
];

const AnalyticsShowcase = () => {
    const [pulseId, setPulseId] = useState("in");

    useEffect(() => {
        const ids = KPIS.map((k) => k.id);
        const t = setInterval(() => {
            setPulseId((prev) => {
                const next = ids[(ids.indexOf(prev) + 1) % ids.length];
                return next;
            });
        }, 2200);
        return () => clearInterval(t);
    }, []);

    return (
        <section data-testid="analytics-showcase" className="relative py-20 md:py-28 bg-[var(--cream)] overflow-hidden">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="max-w-2xl mb-12 md:mb-14"
                >
                    <span className="eyebrow-pill">
                        <i className="fa-solid fa-chart-line text-[10px] accent-blue" />
                        Analytics · Firm intelligence
                    </span>
                    <h2 className="font-display text-[clamp(1.9rem,3.6vw,2.8rem)] leading-[1.05] tracking-tight font-semibold text-[var(--ink)] mt-5">
                        Decisions, <span className="accent-blue">already calibrated.</span>
                    </h2>
                    <p className="mt-5 text-base md:text-lg text-[var(--ink-soft)] leading-relaxed">
                        Live dashboards across intake, conversion, throughput, and source ROI — calibrated to your practice areas, not a generic SaaS template.
                    </p>
                </motion.div>

                {/* Dashboard shell */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
                    className="card-paper p-5 md:p-7"
                    data-testid="analytics-dashboard"
                >
                    {/* Header strip */}
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-5 pb-4 border-b border-[var(--border)]">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[var(--blue)] text-white flex items-center justify-center">
                                <i className="fa-solid fa-chart-line text-sm" />
                            </div>
                            <div>
                                <div className="font-display font-semibold text-[15px] text-[var(--ink)] leading-tight">Firm dashboard · Today</div>
                                <div className="font-mono text-[10px] tracking-wider text-[var(--muted)] uppercase mt-0.5">Updated 12s ago · 4 practice areas</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            {["7D", "30D", "QTD", "YTD"].map((p, i) => (
                                <span
                                    key={p}
                                    className={`px-2.5 py-1 rounded-md font-mono text-[10px] tracking-[0.1em] uppercase ${
                                        i === 1
                                            ? "bg-[var(--ink)] text-[var(--paper)]"
                                            : "bg-[var(--cream-2)] text-[var(--ink-soft)]"
                                    }`}
                                >
                                    {p}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* KPIs */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                        {KPIS.map((k) => (
                            <motion.div
                                key={k.id}
                                whileHover={{ y: -2 }}
                                className={`relative rounded-xl border p-4 transition-all ${
                                    pulseId === k.id
                                        ? "border-[var(--blue)]/40 bg-[rgba(30,91,255,0.04)]"
                                        : "border-[var(--border-strong)] bg-[var(--paper)]"
                                }`}
                                data-testid={`analytics-kpi-${k.id}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="w-7 h-7 rounded-md bg-[var(--cream-2)] flex items-center justify-center text-[var(--ink)]">
                                        <i className={`fa-solid ${k.icon} text-[11px]`} />
                                    </span>
                                    <span
                                        className={`font-mono text-[10px] tracking-wider font-semibold ${
                                            k.trend === "up" ? "text-[#15803D]" : "accent-blue"
                                        }`}
                                    >
                                        {k.delta}
                                        <i className={`fa-solid ${k.trend === "up" ? "fa-arrow-up" : "fa-arrow-down"} text-[9px] ml-1`} />
                                    </span>
                                </div>
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={`${k.id}-${pulseId === k.id ? "p" : "s"}`}
                                        initial={{ opacity: 0, y: 5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -5 }}
                                        transition={{ duration: 0.3 }}
                                        className="font-display font-bold text-[28px] leading-none mt-3 text-[var(--ink)]"
                                    >
                                        {typeof k.value === "number" && k.value < 10 ? k.value.toFixed(1) : k.value.toLocaleString()}
                                        {k.suffix && <span className="text-[16px] text-[var(--ink-soft)] ml-0.5">{k.suffix}</span>}
                                    </motion.div>
                                </AnimatePresence>
                                <div className="font-mono text-[9.5px] tracking-wider text-[var(--muted)] uppercase mt-1.5">{k.label}</div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Funnel + Sources side by side */}
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                        {/* Funnel */}
                        <div className="lg:col-span-3 rounded-xl border border-[var(--border-strong)] bg-[var(--paper)] p-4">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="font-display font-semibold text-[13.5px] text-[var(--ink)]">Intake → Retainer funnel</div>
                                    <div className="font-mono text-[10px] tracking-wider text-[var(--muted)] uppercase mt-0.5">Last 30 days</div>
                                </div>
                                <span className="font-mono text-[10px] tracking-[0.1em] uppercase text-[var(--ink-soft)]">14.8% end-to-end</span>
                            </div>
                            <div className="space-y-2.5">
                                {FUNNEL.map((f, i) => (
                                    <div key={f.id} className="relative">
                                        <div className="flex items-center justify-between text-[12px] mb-1.5">
                                            <span className="text-[var(--ink-soft)]">{f.label}</span>
                                            <span className="font-mono tabular-nums text-[var(--ink)] font-semibold">
                                                {f.value.toLocaleString()} <span className="text-[var(--muted)] font-normal">· {f.pct}%</span>
                                            </span>
                                        </div>
                                        <div className="h-2 rounded-full bg-[var(--cream-2)] overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                whileInView={{ width: `${f.pct}%` }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.9, delay: 0.15 * i, ease: [0.16, 1, 0.3, 1] }}
                                                className="h-full rounded-full"
                                                style={{
                                                    background: i === 0
                                                        ? "linear-gradient(90deg,#1E5BFF,#7C5BFF)"
                                                        : i === FUNNEL.length - 1
                                                            ? "linear-gradient(90deg,#15803D,#22C55E)"
                                                            : "linear-gradient(90deg,#1E5BFF 0%,#1E5BFF 60%,#7C5BFF 100%)",
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Sources */}
                        <div className="lg:col-span-2 rounded-xl border border-[var(--border-strong)] bg-[var(--paper)] p-4">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="font-display font-semibold text-[13.5px] text-[var(--ink)]">Top sources · signed</div>
                                    <div className="font-mono text-[10px] tracking-wider text-[var(--muted)] uppercase mt-0.5">Share of retainers</div>
                                </div>
                            </div>
                            <ul className="space-y-2.5">
                                {SOURCES.map((s, i) => (
                                    <li key={s.id} className="flex items-center gap-3">
                                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
                                        <span className="flex-1 text-[12px] text-[var(--ink-soft)] truncate">{s.label}</span>
                                        <span className="font-mono text-[11px] text-[var(--ink)] font-semibold tabular-nums">{s.value}%</span>
                                        <div className="w-14 h-1.5 rounded-full bg-[var(--cream-2)] overflow-hidden hidden sm:block">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                whileInView={{ width: `${(s.value / 38) * 100}%` }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.7, delay: 0.1 * i }}
                                                className="h-full rounded-full"
                                                style={{ background: s.color }}
                                            />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Cohort table */}
                    <div className="mt-4 rounded-xl border border-[var(--border-strong)] bg-[var(--paper)] p-4 overflow-x-auto">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="font-display font-semibold text-[13.5px] text-[var(--ink)]">Cohort breakdown · by practice area</div>
                                <div className="font-mono text-[10px] tracking-wider text-[var(--muted)] uppercase mt-0.5">Trailing 30 days</div>
                            </div>
                        </div>
                        <table className="w-full text-[12.5px] min-w-[520px]">
                            <thead>
                                <tr className="font-mono text-[10px] tracking-wider text-[var(--muted)] uppercase">
                                    <th className="text-left pb-2">Practice area</th>
                                    <th className="text-right pb-2">Signed</th>
                                    <th className="text-right pb-2">Conv. rate</th>
                                    <th className="text-right pb-2">Pipeline</th>
                                </tr>
                            </thead>
                            <tbody>
                                {COHORTS.map((c, i) => (
                                    <motion.tr
                                        key={c.id}
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: 0.05 * i }}
                                        className="border-t border-[var(--border)]"
                                    >
                                        <td className="py-2.5 text-[var(--ink)] font-semibold">{c.label}</td>
                                        <td className="py-2.5 text-right tabular-nums text-[var(--ink-soft)] font-mono">{c.signed}</td>
                                        <td className="py-2.5 text-right tabular-nums">
                                            <span
                                                className={`font-mono ${c.rate >= 15 ? "text-[#15803D]" : "text-[var(--ink-soft)]"}`}
                                            >
                                                {c.rate}%
                                            </span>
                                        </td>
                                        <td className="py-2.5 text-right tabular-nums text-[var(--ink)] font-semibold font-mono">{c.value}</td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default AnalyticsShowcase;

